import { useNavigate, useParams } from 'react-router';
import React, { ReactElement, useState } from 'react';
import {
  Button, Col, FormGroup, Row, LoaderButton,
} from 'idsk-react-components';
import { useSearchParams } from 'react-router-dom';
import { Message } from 'slovlex-idsk-react-components';
import ClassType from '../../model/pripomienka/ClassType';
import AddVyhodnotenie from '../../model/pripomienka/AddVyhodnotenie';
import HromadneVyhodnotenie from '../../model/HromadneVyhodnotenie';
import { useSuccessMessageContext } from '../../context/SuccessMessageContext';
import { useClient } from '../../context/ClientStatusContext';
import useFieldErrorMessages from '../useFieldErrorMessages';
import { hromadneVyhodnotit } from '../../client/hromadneVyhodnotenieClient';
import { SafeParameterSetter } from '../../types/SafeParameterSetter';
import Uuid from '../../model/Uuid';
import VyhodnotenieSelectBox from '../VyhodnotenieForm/component/VyhodnotenieSelectBox';
import VyhodnotenieText from '../VyhodnotenieForm/component/VyhodnotenieText';
import routes from '../../routing/routes';
import StavPripomienky from '../../model/pripomienka/StavPripomienky';
import './hromadneVyhodnotenieComponent.scss';
import { useHistoryContext } from '../../context/HistoryContext';

interface Props{
  classType: ClassType;
}

const HromadneVyhodnotenieComponent = ({ classType }: Props): ReactElement => {
  const {
    type,
    year,
    index,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number }>();
  const [searchParams] = useSearchParams();
  const getPripomienkyUuids = (): Uuid[] => {
    const uuidsString = searchParams?.get('pripomienkyUuids');

    if (!uuidsString) {
      return [];
    }
    return uuidsString.split(',');
  };
  const navigate = useNavigate();
  const { lastVisitedListPage } = useHistoryContext();

  const [vyhodnotenie, setVyhodnotenie] = useState<HromadneVyhodnotenie>(
    { pripomienkaUuidList: getPripomienkyUuids(), stavPripomienky: StavPripomienky.AKCEPTOVANA, textVyhodnotenia: '' },
  );

  const { status, wrapClient: saveClient } = useClient(hromadneVyhodnotit);
  const { displaySuccess, clearMessages } = useSuccessMessageContext();

  const { clearFieldErrorMessage, getFieldErrorMessage } = useFieldErrorMessages();
  const updateField: SafeParameterSetter<AddVyhodnotenie> = (fieldName, value) => {
    setVyhodnotenie(prevState => ({ ...prevState, [fieldName]: value }));
    clearFieldErrorMessage(fieldName);
  };
  const handleSubmit = async (e): Promise<void> => {
    e.preventDefault();
    await saveClient(classType, type, year, index, vyhodnotenie);
    navigate(lastVisitedListPage || getBackUrl());
    displaySuccess('Vyhodnotenie pripomienok bolo úspešne uložené.');
  };

  const getBackUrl = (): string => (classType === ClassType.HROMADNA_PRIPOMIENKA
    ? routes.hromadnePripomienky.list.withParams(type, year, index)
    : routes.pripomienky.list.withParams(type, year, index));

  const pripomienkyNumberLabel = (): string => {
    if (!getPripomienkyUuids() || getPripomienkyUuids()?.length === 0) {
      return `Vyhodnocujete 0 ${classType === ClassType.HROMADNA_PRIPOMIENKA ? 'hromadných' : ''} pripomienok`;
    }
    if (getPripomienkyUuids()?.length === 1) {
      return `Vyhodnocujete 1 ${classType === ClassType.HROMADNA_PRIPOMIENKA ? 'hromadnú' : ''} pripomienku`;
    }
    if (getPripomienkyUuids()?.length > 1 && getPripomienkyUuids()?.length < 5) {
      return `Vyhodnocujete 
      ${getPripomienkyUuids().length} ${classType === ClassType.HROMADNA_PRIPOMIENKA ? 'hromadné' : ''}
       pripomienky`;
    }
    return `Vyhodnocujete
     ${getPripomienkyUuids().length} ${classType === ClassType.HROMADNA_PRIPOMIENKA ? 'hromadných' : ''}
      pripomienok`;
  };

  return (
    <section className="hromadne-vyhodnotenie">
      <Row>
        <Col>
          {getFieldErrorMessage('pripomienkaUuidList')
            && (
            <Message
              className="client-error-message"
              type="error"
              headingSize="m"
              onClear={clearMessages}
            >
              { getFieldErrorMessage('pripomienkaUuidList') }
            </Message>
            )}
        </Col>
      </Row>
      <Row>
        <Col>
          <FormGroup>{pripomienkyNumberLabel()}</FormGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          <VyhodnotenieSelectBox stavFromDetail={vyhodnotenie.stavPripomienky} updateField={updateField} />
        </Col>
      </Row>
      <Row>
        <Col>
          <VyhodnotenieText
            textFromDetail={vyhodnotenie.textVyhodnotenia}
            updateField={updateField}
          />
        </Col>
      </Row>
      <Row>
        <Col className="hromadne-vyhodnotenie__actions">
          <LoaderButton
            loading={status.loading}
            onClick={handleSubmit}
            size="large"
          >Uložiť
          </LoaderButton>
          <Button
            className="cancel-button"
            buttonStyle="secondary"
            size="large"
            onClick={() => navigate(getBackUrl())}
          >Zrušiť
          </Button>
        </Col>
      </Row>
    </section>
  );
};

export default HromadneVyhodnotenieComponent;
